import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useSearchParams } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  DEFAULT_SIGNATURE_MESSAGE,
  DEFAULT_SIGNATURE_SUBJECT,
  DOCUSIGN_TOKENS,
  DOCUSIGN_TOKEN_LABELS,
  tokenPlaceholder,
} from '@/lib/docusign-templates';
import { formatDateTime, orDash } from '@/lib/format';
import { isAdminRole, roleLabel } from '@/lib/roles';
import { useAuth } from '@/lib/use-auth';
import {
  denialReason,
  PERMISSION_SET_REFUSAL,
  type Ability,
  type ModuleSlug,
} from '@/lib/permissions';
import type {
  DocuSignEmailTemplates,
  DocuSignEmailTemplatesUpdateInput,
  Paginated,
  QuoteTermsSettings,
  SmtpSettings,
  User,
} from '@/lib/types';
import { B2bProposalRoutingCard } from '@/components/settings/B2bProposalRoutingCard';
import { NotificationRulesCard } from '@/components/settings/notifications/NotificationRulesCard';
import { NotificationDefaultsCard } from '@/components/settings/notifications/NotificationDefaultsCard';
import { EnquiryAlertRecipientsCard } from '@/components/settings/EnquiryAlertRecipientsCard';
import { EnquiryAssigneesCard } from '@/components/settings/EnquiryAssigneesCard';
import { SheetFillColoursCard } from '@/components/settings/SheetFillColoursCard';
import { ExchangeRateSettings } from '@/components/settings/ExchangeRateSettings';
import { SupportSessionDialog } from '@/components/support/SupportSessionDialog';
import { canOfferSupportSessions, supportSessionDenial } from '@/lib/support-session';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice as UiNotice } from '@/components/ui/Notice';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { EmailLink } from '@/components/ui/TextLink';
import { usePageLayout, type PageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';

/*
 * Settings — the server-side configuration SEED EMERGE can REPORT ON.
 *
 * Shaped as a column of cards rather than as "the email screen", for the same
 * reason `IntegrationsPage` is: a second setting belongs here as another card,
 * not as another route. There is deliberately no placeholder card for anything
 * that does not exist — an empty "coming soon" panel is a promise nobody made.
 *
 * ⚠️ **CORRECTED 2026-08-30. This header used to read *"NOTHING ON THIS PAGE
 * WRITES CONFIGURATION, and that is structural rather than a gap to fill in
 * later"*, and it is no longer true.** It is written as a correction rather than
 * quietly edited because that sentence was load-bearing: a reader who carried it
 * away would conclude the quote-terms card below had acquired a write by
 * accident, and might "restore" the property by removing it.
 *
 * What was true, and still is, is the sentence it was standing in for: **the
 * SMTP credentials are not writable and must never become so.** Host, port, user
 * and password are deployment configuration living in the server's env file; an
 * endpoint that edits them is a way to take every outbound email down from a
 * browser. See the header of the Email card, which is unchanged.
 *
 * **The default quote terms are the opposite case on both counts** — wording
 * printed on a customer document, which changes when the business changes, not
 * when the deployment does. So one setting on this page is writable and the
 * other is not, and the distinction is *business data versus deployment
 * configuration*, never *settings are editable now*.
 */

/* --- shared bits --------------------------------------------------------- */

/**
 * Something worth someone's attention. `[overflow-wrap:anywhere]` because an API
 * message can carry an unbroken host name, address or SMTP response, and this
 * page is tested at 320px.
 *
 * A thin wrapper over the shared `ui/Notice` (2026-10-11, UI rework): it keeps
 * only the tone-to-role rule below, which is this page's, and renders through
 * the primitive so the look is the app's one callout.
 *
 * ⚠️ **The two tones differ in ROLE as well as colour, and that is the reason
 * the prop exists.** `danger` is `role="alert"` — a request failed and the
 * reader is mid-action. `warning` is `role="status"`, because a queue backlog is
 * a standing condition nobody just triggered, and firing an assertive live
 * region at somebody who merely opened the page trains them to dismiss it.
 */
function Notice({
  tone = 'danger',
  title,
  children,
}: {
  tone?: 'danger' | 'warning';
  title: string;
  children: ReactNode;
}) {
  return (
    <UiNotice tone={tone} role={tone === 'danger' ? 'alert' : 'status'} title={title}>
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </UiNotice>
  );
}

/** One labelled fact. A `dl` rather than a table — these are pairs, not rows. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/** The empty-state glyph: an envelope. */
function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="m3 7 8.4 5.6a1 1 0 0 0 1.2 0L21 7" />
    </svg>
  );
}

/* --- gating -------------------------------------------------------------- */

/*
 * `GET | POST /settings/smtp*` are ADMIN TIER — **the read as well as the test
 * send**; see the methods in `@/lib/api`.
 *
 * ⚠️ **The read being admin tier is what makes the sidebar row `adminOnly`.**
 * This page shipped following `/integrations` — every role may look, the page
 * gates its own write — and that was correct against the endpoint as first
 * described. The endpoint as BUILT 403s below admin even to read, which is the
 * `/audit-log` shape, so the row is hidden rather than leading every
 * `basic_user` to a card that can only refuse them. The 403 is still handled
 * below: hiding a row is an optimisation (the URL is typeable, and a role can be
 * taken away under a token that lasts over five days), never a guarantee.
 *
 * ✅ **A `settings` MODULE EXISTS NOW — 2026-09-02.** *SUPERSEDED: "There is no
 * `settings` module in `ModuleSlug`, and one is NOT invented here … adding a
 * member would make this page ask `/permissions/me` a question nobody has ever
 * put to the server."* That was right about inventing one and is not a reason to
 * refuse a real one: the server publishes `settings` now, so the question has an
 * answer. (The same paragraph's aside about `/targets` was superseded by the
 * same migration — it is a real module too, and is in the union.)
 *
 * ⚠️ **THE ADMIN TIER IS THE FLOOR AND THE PERMISSION IS A NARROWER FILTER ON
 * TOP — never the other way round.** `GET /settings/smtp` is admin tier on the
 * READ, so below admin the only possible answer is a refusal; a set granting
 * `settings.*` to a `basic_user` must not open anything, or this page renders
 * with every request 403ing. `enforcedByRole` answers the role first for exactly
 * this, and the gate below says both halves out loud rather than relying on it.
 *
 * ⚠️ **So the permission half is INERT TODAY, and that is worth knowing before
 * somebody unticks a box and waits for something to happen.** `can()`
 * short-circuits for the admin tier — a permission list never narrows an admin —
 * and admins are the only people who can reach this control at all. Unticking
 * `settings.update` on a set therefore hides NOTHING here. It is written anyway
 * because it is the true expression of the rule and stops being inert the moment
 * either half of that changes; it is not a control anybody should be told works.
 *
 * ⚠️ **And a NEIGHBOURING slug would still be worse than either.**
 * `companies.update` is what `IntegrationsPage` used as its proxy until this
 * same day, and copying it here would have been a real defect rather than an
 * inelegance: `companies.update` is held by `basic_user`, so the button would be
 * enabled for people the API answers `403`. That is the over-reporting direction
 * `@/lib/permissions` exists to prevent.
 *
 * Gated through the ability object, never `user.role`.
 *
 * The sentence below stays written out rather than routed through
 * `denialReason()`: that helper words a `settings` denial as "Editing this is
 * restricted to administrators", which is true and does not name the test send.
 * It matches the helper's admin wording deliberately, so the app says one thing
 * about one situation.
 */
/*
 * 🔴 2026-10-01: the set decides for every role (owner's decision), so every
 * card on this page asks `settings.update` alone and words a refusal with the
 * one neutral sentence. *SUPERSEDED: the admin tier as the floor, and
 * "Sending a test message is restricted to administrators." / "read-only
 * access" / "does not have permission" as three role-shaped sentences.*
 */
const ADMIN_ONLY_DENIAL = PERMISSION_SET_REFUSAL;

// 2026-10-02 (owner): the mailer is its own module, `settings-email`.
function testDenialFor(ability: Ability): string | null {
  return denialReason(ability, 'update', 'settings-email');
}

/* --- the mailer's status ------------------------------------------------- */

interface StatusState {
  data: SmtpSettings | null;
  /**
   * When `data` was read, as an epoch millisecond stamp — the clock the queue's
   * age is measured against.
   *
   * ⚠️ **Captured on arrival rather than read at render**, and not only because
   * `Date.now()` in a render body is impure: the age is a property of the
   * READING, so measuring it against a later clock makes the queue silently
   * older every time an unrelated keystroke re-renders the card. A stale figure
   * that says when it was taken beats a live one nobody asked for.
   */
  fetchedAt: number | null;
  loading: boolean;
  error: string | null;
  /** A 404 — the API predates this endpoint. Version skew, not a failure. */
  missing: boolean;
  /** A 403 — the read is admin tier. A permission boundary, not a failure. */
  forbidden: boolean;
}

const IDLE_STATUS: StatusState = {
  data: null,
  fetchedAt: null,
  loading: true,
  error: null,
  missing: false,
  forbidden: false,
};

/**
 * What the status read said about `configured` — THREE answers, not two.
 *
 * ⚠️ **A missing key is UNKNOWN and must never collapse into "no".**
 * `!configured` on an `undefined` is `true`, and the obvious rendering therefore
 * announces **"email is not configured"** about a perfectly healthy mailer — a
 * false statement about infrastructure that sends somebody to "fix" working
 * configuration. The value is read through an `unknown` so the third branch is
 * visibly decided rather than inherited from JavaScript's truthiness, the same
 * way `canApplyToRevenue` is handled in `@/lib/xero-tax-rates`.
 *
 * The endpoint's shape is no longer unverified — see {@link SmtpSettings} — so
 * this branch is now about a server older or stranger than the contract, not
 * about a guess. It is kept because the cost is one badge and the failure it
 * prevents is silent.
 */
type Configured = 'yes' | 'no' | 'unknown';

function configuredOf(settings: SmtpSettings): Configured {
  const raw: unknown = settings.configured;
  if (typeof raw !== 'boolean') return 'unknown';
  return raw ? 'yes' : 'no';
}

/**
 * The reasons the server gave, by env key — or `null` when it reported none at
 * all, which is NOT the same as reporting an empty list.
 *
 * Same guard, same reason: `problems` absent yields `undefined`, and rendering
 * that as "nothing to report" beside "not configured" claims the server was
 * asked and had no answer.
 */
function problemsOf(settings: SmtpSettings): string[] | null {
  const raw: unknown = settings.problems;
  if (!Array.isArray(raw)) return null;
  const lines = raw.filter((line): line is string => typeof line === 'string' && line.trim() !== '');
  return lines.length > 0 ? lines : null;
}

/** `port` is a number, so `orDash` (which takes strings) cannot render it. */
function portLabel(port: number | null): string {
  return port === null ? '—' : String(port);
}

/* --- the outbound queue -------------------------------------------------- */

/**
 * How old the oldest queued message must be before this card says anything.
 *
 * ⚠️ **A non-empty queue is the NORMAL state, so the naive check — warn whenever
 * `oldestPendingEmailAt` is non-null — fires on every healthy server that has
 * just sent anything.** The worker drains on an interval; a few seconds of
 * backlog is the system working.
 *
 * **Why fifteen minutes.** The drain interval is a server-side detail this app
 * does not know and must not encode, so the threshold is deliberately set far
 * longer than any plausible one rather than tuned to a measured value: anything
 * still queued after fifteen minutes has survived many drain cycles, which is a
 * stuck worker or a relay refusing every attempt, not latency.
 *
 * ⚠️ **It errs LATE on purpose, and that asymmetry is the whole point.** This
 * card is the only place a broken mailer is visible, so its credibility is the
 * feature: the first false alarm on a healthy queue teaches everybody to ignore
 * the warning, and the true one is then dismissed too. A missed warning still
 * leaves the reader the test-send button one paragraph below it; a false warning
 * removes the value of both. Same direction as `changedSinceLastPush` on
 * `InvoiceDetailPage` — **under-reporting into silence is honest, over-reporting
 * is a false statement.**
 */
const QUEUE_BACKLOG_WARNING_MS = 15 * 60 * 1000;

/**
 * The queue's age in milliseconds, or `null` when there is nothing to measure.
 *
 * ⚠️ **A NEGATIVE age is discarded, not reported.** The timestamp is the
 * server's and the clock is the browser's, so a queue entry can legitimately
 * read as being in the future by a few seconds of skew — and a negative number
 * must neither pass an `age >= threshold` test nor be shown as a fault of its
 * own. An unparseable timestamp goes the same way: **we cannot tell** is not
 * **there is a backlog**.
 */
function queueAgeMs(oldestPendingEmailAt: string | null, now: number): number | null {
  if (oldestPendingEmailAt === null) return null;
  const queuedAt = new Date(oldestPendingEmailAt).getTime();
  if (Number.isNaN(queuedAt)) return null;
  const age = now - queuedAt;
  return age >= 0 ? age : null;
}

/* --- the test send ------------------------------------------------------- */

/**
 * The outcome of a test send.
 *
 * ⚠️ **`sent` is deliberately not called `delivered`, and its copy must never
 * say the message arrived.** A `204` means the server handed the message to the
 * mail host without an error; it says nothing about spam filtering, a rejected
 * recipient, or a bounce that comes back ten minutes later. Same class of claim
 * as a `200` from Xero, which this codebase has been wrong about three times: an
 * accepted request is not a stored value, and it is not a delivered one. The
 * only confirmation is a person opening an inbox.
 */
type TestOutcome =
  | { kind: 'sent'; to: string }
  | {
      kind: 'failed';
      /** The SERVER's sentence, verbatim. Never paraphrased, never replaced. */
      message: string;
      /**
       * The underlying cause, where the server sent one — `details[].reason`,
       * read through `ApiError.detailReasons`.
       *
       * ⚠️ **On `MAIL_SEND_FAILED` this is the whole diagnosis, and `message`
       * is a fixed literal that cannot name the fault.** *"The mail server
       * refused it"* and *"Invalid login: 535-5.7.8 Username and Password not
       * accepted"* are two different, simultaneously-true facts — what happened
       * and why — so this is a field of its own rather than something folded
       * into `message`. Folding it in is the `a ?? b` drop; picking one is
       * worse, because the half that reads best is the half that says nothing.
       *
       * **Empty is ordinary and renders as nothing**: a `reason` of `null`, an
       * error with no details, or a server older than the field, whose
       * `message` still carries the diagnosis itself. No empty slot, no "null",
       * and no sentence of ours claiming the server said nothing — on that
       * older server it would be false.
       */
      reasons: string[];
      fieldError: string | null;
      /**
       * Ours, rendered BESIDE the server's sentence and never in front of it —
       * where the reader has to go, which the server's message cannot know.
       * `null` whenever we have nothing to add that the message does not say.
       */
      note: string | null;
    };

/*
 * The two refusal codes `POST /settings/smtp/test` carries. **Matched on
 * `error.code`, never on message text** — the house rule everywhere on this API,
 * and the reason is sharper than usual here: the relay's own words reach this
 * screen verbatim, so they are the strings in the app most certain to be
 * rewritten by somebody else's server. They arrive in `details[].reason` as of
 * 2026-08-29; before that they were interpolated into `message`, and a matcher
 * written against either one would have broken on the other.
 *
 * An unrecognised code falls through to the server's message deliberately, the
 * way `quoteRefusalOf` does in `@/lib/xero`: unknown means *newer than this
 * branch*, never *not a refusal*.
 */
const MAIL_NOT_CONFIGURED_CODE = 'MAIL_NOT_CONFIGURED';
const MAIL_SEND_FAILED_CODE = 'MAIL_SEND_FAILED';

function EmailCard() {
  const { ability } = useAuth();
  const headingId = useId();

  const [status, setStatus] = useState<StatusState>(IDLE_STATUS);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<TestOutcome | null>(null);

  const denial = testDenialFor(ability);
  const canSend = denial === null;

  /*
   * Nothing here sets `loading` on the way IN, only on the way out: the state
   * starts loading and the effect runs once per mount, so a synchronous
   * `setState` would only start a second render to write a value already there.
   */
  const loadStatus = useCallback((signal: AbortSignal) => {
    return api
      .getSmtpSettings(signal)
      .then((response) => {
        if (signal.aborted) return;
        setStatus({
          data: response.data,
          fetchedAt: Date.now(),
          loading: false,
          error: null,
          missing: false,
          forbidden: false,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * A 404 keeps its calm state, and it is NO LONGER the normal case: the
         * route exists as of 2026-08-29, so a 404 now means this build is
         * talking to a server older than the endpoint. Kept for exactly that,
         * the way `/integrations` keeps a branch for an entity the backend has
         * not wired — version skew is an ordinary thing to be in the middle of,
         * and rendering it as a server fault sends somebody debugging a mailer
         * that is fine.
         *
         * ⚠️ A 403 is a THIRD state and must not fall through to the error
         * branch. The read is admin tier, so a non-admin who reaches this page
         * gets a refusal, not a failure — and "Couldn't read the mail settings"
         * in danger red describes a broken API to somebody whose account is
         * simply not allowed to look.
         *
         * A 401 is left entirely to `@/lib/api`, which already attempts one
         * refresh and then ends the session; reporting it would put a second,
         * contradictory message on screen midway through signing out.
         */
        if (caught instanceof ApiError && caught.status === 404) {
          setStatus({
            data: null,
            fetchedAt: null,
            loading: false,
            error: null,
            missing: true,
            forbidden: false,
          });
          return;
        }
        if (caught instanceof ApiError && caught.status === 403) {
          setStatus({
            data: null,
            fetchedAt: null,
            loading: false,
            error: null,
            missing: false,
            forbidden: true,
          });
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setStatus({
          data: null,
          fetchedAt: null,
          loading: false,
          error:
            caught instanceof Error ? caught.message : 'Could not read the outbound mail settings.',
          missing: false,
          forbidden: false,
        });
      })
      .finally(() => {
        if (signal.aborted) return;
        setStatus((current) => ({ ...current, loading: false }));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadStatus(controller.signal);
    return () => controller.abort();
  }, [loadStatus]);

  const trimmedTo = to.trim();

  function sendTest() {
    /*
     * `sending` is the double-fire guard as well as the disabled reason: the
     * request has no idempotency key, so two clicks are two messages. It is set
     * before the call and cleared in `finally`, never on either branch alone.
     */
    if (sending || trimmedTo === '') return;
    setSending(true);
    setOutcome(null);
    api
      .testSmtpSettings({ to: trimmedTo })
      .then(() => {
        setOutcome({ kind: 'sent', to: trimmedTo });
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof ApiError)) {
          setOutcome({
            kind: 'failed',
            message: caught instanceof Error ? caught.message : 'Could not send the test message.',
            reasons: [],
            fieldError: null,
            note: null,
          });
          return;
        }
        /*
         * A 403 means the role changed under a token that lasts over five days,
         * so the gate above was an optimisation rather than a guarantee. It
         * carries no `details`, unlike a 422, so it stays a banner and never
         * becomes a per-field message.
         */
        if (caught.status === 403) {
          setOutcome({
            kind: 'failed',
            message: caught.message,
            reasons: caught.detailReasons,
            fieldError: null,
            note: ADMIN_ONLY_DENIAL,
          });
          return;
        }
        if (caught.status === 404) {
          setOutcome({
            kind: 'failed',
            message: caught.message,
            reasons: caught.detailReasons,
            fieldError: null,
            note: 'This API has no test-send route, so nothing was sent. The server is older than this build.',
          });
          return;
        }
        /*
         * ⚠️ **The relay's own words, rendered VERBATIM — and since 2026-08-29
         * they arrive in `details[].reason`, not in `message`.** *"Invalid
         * login: 535-5.7.8 Username and Password not accepted"* names the
         * actual fault; `message` is now the fixed sentence *"Sending the test
         * message failed. The mail server refused it."*, which is precisely the
         * tidy "sending failed" that makes the button pointless on its own. So
         * **both go on screen**: `message` says what happened, `reasons` says
         * why, and rendering either alone drops a true and useful half.
         *
         * The move is not cosmetic — the backend may expose a 5xx message only
         * when it is a compile-time literal, because interpolating the
         * transport's text made this site's credential safety depend on another
         * function continuing to strip the SMTP `command`/`response` fields,
         * which during an auth failure carry the base64 username and password.
         *
         * ⚠️ **Still no `note`, and the reason is now stronger.** An older
         * server sends the whole diagnosis in `message` and no `reason` at all,
         * so this branch serves both shapes — and any sentence of ours about
         * the server "not saying why" would be flatly false against the old
         * one. Empty `reasons` renders as nothing, which is honest under both.
         */
        if (caught.code === MAIL_SEND_FAILED_CODE) {
          setOutcome({
            kind: 'failed',
            message: caught.message,
            reasons: caught.detailReasons,
            fieldError: null,
            note: null,
          });
          return;
        }
        /*
         * Not a fault of the address, and not something a retry fixes. The
         * server's sentence says the config is missing; ours says where the
         * missing keys are named, which is the card directly above.
         */
        if (caught.code === MAIL_NOT_CONFIGURED_CODE) {
          setOutcome({
            kind: 'failed',
            /*
             * ⚠️ **Untouched by the 502 change, deliberately.** The masking rule
             * is a 5xx one — below 500 nothing is masked — so this `message` is
             * still the real sentence naming the missing env keys. Reading it
             * out of `reasons` instead would render an empty banner.
             */
            message: caught.message,
            reasons: caught.detailReasons,
            fieldError: null,
            note: 'Nothing was sent. The reasons above name the environment keys the server still needs.',
          });
          return;
        }
        /*
         * Ordered by SPECIFICITY, never by which shape arrived first — a
         * better-shaped error landing on an endpoint you already handle is a
         * breaking change, and reading `formMessage` ahead of `fieldErrors`
         * silently prefers the vaguer sentence the day the server improves.
         *
         * `to` is the only key this form renders, so anything else the server
         * names is unroutable and belongs in the banner rather than on the
         * input, where it would inflate the count and highlight nothing.
         */
        const fieldError = caught.fieldErrors['to'] ?? null;
        setOutcome({
          kind: 'failed',
          message: fieldError ?? caught.formMessage ?? caught.message,
          /*
           * Read here too, not only on the code we recognise: an unrecognised
           * code means *newer than this branch*, never *not a refusal*, and a
           * future masked 5xx will carry its diagnosis the same way. Empty on
           * every ordinary 422, so this costs nothing when there is none.
           */
          reasons: caught.detailReasons,
          fieldError,
          note: null,
        });
      })
      .finally(() => setSending(false));
  }

  const settings = status.data;
  const configured = settings ? configuredOf(settings) : null;
  const problems = settings ? problemsOf(settings) : null;
  /*
   * Measured against the clock at the moment of the READ, not a ticking one: a
   * queue crossing the threshold while somebody looks at the page is not worth
   * a re-render every second, and a reload answers it.
   */
  const queueAge =
    settings && status.fetchedAt !== null
      ? queueAgeMs(settings.oldestPendingEmailAt, status.fetchedAt)
      : null;
  const queueIsBacklogged = queueAge !== null && queueAge >= QUEUE_BACKLOG_WARNING_MS;

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Email" />
      <PanelBody className="flex flex-col gap-4">
        {status.error && <Notice title="Couldn’t read the mail settings.">{status.error}</Notice>}

        {status.loading && !settings ? (
          /* Skeletons in the real shape — a badge, then the grid of facts. */
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-40" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        ) : status.forbidden ? (
          /*
           * The read is admin tier, so this is a boundary rather than a fault.
           * The sidebar row is `adminOnly` and this page is normally unreachable
           * without the tier — somebody arrived by URL, or lost the role under a
           * live token. Everything below is skipped: the test send would only
           * collect a second 403.
           */
          <EmptyState
            icon={<MailIcon />}
            title="Mail settings are not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        ) : status.missing ? (
          /*
           * ⚠️ **No longer the normal case.** `/settings/smtp` exists as of
           * 2026-08-29, so a 404 here means this build is talking to a server
           * older than the endpoint. The branch is KEPT for exactly that, the
           * way `/integrations` keeps one for an entity the backend has not
           * wired — but the copy no longer says the feature is unbuilt, which
           * would now be a sentence asserting the absence of something that
           * exists. Everything below is skipped: a test send against a route
           * this server does not have would only produce a second 404.
           */
          <EmptyState
            icon={<MailIcon />}
            title="This API has no mail settings endpoint"
            description="The server is running a build older than this one."
          />
        ) : settings ? (
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {configured === 'yes' && (
                <Badge tone="success" dot>
                  Configured
                </Badge>
              )}
              {/*
               * NOT a danger tone. Shipping before the credential exists is the
               * design — the env keys are optional precisely so the release does
               * not wait on a server change — so this is an ordinary state being
               * reported, not a fault being raised.
               */}
              {configured === 'no' && <Badge tone="neutral">Not configured</Badge>}
              {/*
               * The third answer, and the one that must not borrow either of the
               * others' sentences. "We could not read this" is not an all-clear
               * and is not a denial.
               */}
              {configured === 'unknown' && <Badge tone="warning">Status not reported</Badge>}
            </div>

            {/*
             * ⚠️ **The actionable half of "not configured", and the reason it is
             * rendered at all.** Each line names the exact environment key the
             * server is missing — `SMTP_HOST is not set` — so an operator knows
             * what to add to `shared/.env`. "Email is not configured" on its own
             * is a true statement nobody can act on.
             *
             * These are diagnostics, not helper text: the no-helper-text
             * prohibition's exception list keeps errors and refusals by name,
             * and the test for any sentence is *what wrong conclusion does this
             * prevent?* — here, that the configuration is broken in some
             * unknowable way.
             *
             * `problemsOf` returns `null` for "the server listed none", which is
             * NOT "there are none": an absent list beside a "not configured"
             * badge would claim the server had nothing to say. Nothing is
             * rendered in that case, and the sentence above still stands.
             */}
            {configured === 'no' && problems !== null && (
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {problems.map((problem) => (
                  <li
                    key={problem}
                    className="min-w-0 text-xs text-fg-secondary [overflow-wrap:anywhere]"
                  >
                    <span aria-hidden="true" className="mr-2 text-fg-subtle">
                      •
                    </span>
                    {problem}
                  </li>
                ))}
              </ul>
            )}

            {/*
             * ⚠️ **`configured` and WORKING are different questions, and this is
             * the only field that separates them.** Perfect credentials behind a
             * dead queue worker read as `configured: true` with an empty
             * `problems` while approval mail piles up unsent — and every other
             * screen in the app looks healthy throughout.
             *
             * Only an AGED queue is rendered. See {@link QUEUE_BACKLOG_WARNING_MS}
             * for the threshold and why it errs late: a warning that fires on the
             * few seconds of backlog a healthy server always carries is a warning
             * everybody learns to ignore, and this card cannot afford that.
             */}
            {queueIsBacklogged && settings.oldestPendingEmailAt !== null && (
              <Notice tone="warning" title="Mail is queued but not going out.">
                {`Oldest message queued ${formatDateTime(settings.oldestPendingEmailAt)}.`} The
                sending worker is stopped or the mail host is refusing every attempt.
              </Notice>
            )}

            <dl className="grid gap-3 sm:grid-cols-2">
              {/* What a recipient sees in their From line. */}
              <Fact label="Sends from">{orDash(settings.fromAddress)}</Fact>
              <Fact label="Mail host">{orDash(settings.host)}</Fact>
              <Fact label="Port">{portLabel(settings.port)}</Fact>
              {/*
               * Stated as a value, not as advice — a few seconds of queue is the
               * system working, so the fact belongs on screen while the warning
               * above does not. `queueAge` is null for an absent, unparseable or
               * future timestamp, and "nothing waiting" would be a claim in the
               * last two cases, so those read as unreported.
               */}
              <Fact label="Outbound queue">
                {settings.oldestPendingEmailAt === null
                  ? 'Nothing waiting'
                  : queueAge === null
                    ? '—'
                    : `Oldest queued ${formatDateTime(settings.oldestPendingEmailAt)}`}
              </Fact>
            </dl>

            {/* --- test send --- */}
            <div className="flex flex-col gap-2 border-t border-border pt-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <Field label="Send a test message to" error={outcome?.kind === 'failed' ? outcome.fieldError : null}>
                    {(fieldProps) => (
                      <Input
                        {...fieldProps}
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={to}
                        invalid={outcome?.kind === 'failed' && outcome.fieldError !== null}
                        disabled={!canSend}
                        onChange={(event) => {
                          setTo(event.target.value);
                          // A previous result describes a previous address.
                          if (outcome) setOutcome(null);
                        }}
                      />
                    )}
                  </Field>
                </div>
                <Button
                  variant="secondary"
                  className="shrink-0 max-sm:w-full"
                  disabled={!canSend || sending || trimmedTo === ''}
                  title={denial ?? undefined}
                  onClick={sendTest}
                >
                  {sending ? 'Sending…' : 'Send test'}
                </Button>
              </div>

              {/*
               * The reason is text as well as a `title`: a disabled button has
               * `pointer-events: none`, so its tooltip never appears for the
               * person it is meant for.
               */}
              {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}

              {outcome?.kind === 'sent' && (
                <p role="status" className="m-0 text-xs text-fg-secondary">
                  <span className="font-medium text-fg">
                    Handed to the mail server for {outcome.to}.
                  </span>{' '}
                  Not the same as delivered. Check the inbox and the spam folder.
                </p>
              )}

              {/*
               * A field error is already on the input, so the banner would
               * repeat it. Everything else has no control to sit on.
               *
               * ⚠️ **THREE halves render when all three exist, in this order.**
               * `message ?? reason` or `message ?? note` would each be a silent
               * drop — they are simultaneously-true statements about different
               * things, so none may stand in for another:
               *
               * - `message` — the server's own sentence, what happened;
               * - `reasons` — the server's own diagnosis, WHY. On
               *   `MAIL_SEND_FAILED` this is the only actionable text there is,
               *   because `message` is a fixed literal that cannot name the
               *   fault. Verbatim, on its own line, never paraphrased;
               * - `note` — ours, where the reader has to GO. Last, because the
               *   server's words outrank ours and must not be pushed down.
               *
               * `reasons` empty renders nothing at all — a `reason` of `null`,
               * an error with no details, or a server old enough that `message`
               * still carries the diagnosis itself. An empty line, or the word
               * "null", would each claim something the server never said.
               *
               * The lead-in is a fact about provenance and stays under the
               * no-helper-text rule's exception for refusals: without it a bare
               * `connect ETIMEDOUT` reads as SEED EMERGE's own error text
               * rather than as the mail host's reply.
               */}
              {outcome?.kind === 'failed' && outcome.fieldError === null && (
                <Notice title="Couldn’t send the test message.">
                  {outcome.message}
                  {outcome.reasons.map((reason) => (
                    <span key={reason} className="mt-1 block">
                      What the mail server reported: <span className="font-medium">{reason}</span>
                    </span>
                  ))}
                  {outcome.note !== null && <> {outcome.note}</>}
                </Notice>
              )}
            </div>
          </>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

/* --- support sessions ---------------------------------------------------- */

/**
 * How many people the search shows at once.
 *
 * Small on purpose. This is a *find one person* control, not a second staff
 * directory — `/users` is that, and it pages, sorts and filters. Anything the
 * first handful misses is answered by typing more of the name, and the count
 * below says when there is more than this behind the search.
 */
const SUPPORT_SEARCH_PER_PAGE = 8;

function LifebuoyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="m5.6 5.6 3.85 3.85" />
      <path d="m14.55 14.55 3.85 3.85" />
      <path d="m18.4 5.6-3.85 3.85" />
      <path d="m9.45 14.55-3.85 3.85" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/**
 * The second entry point to a support session — find somebody, say why, start.
 *
 * The row action on `/users` is the fast path for a person already looking at
 * the directory; this is where the feature is discoverable for somebody who
 * only knows they need it. The RULES are not restated here: they live once in
 * `@/lib/support-session`, so this card and that row cannot come to different
 * conclusions about who may act as whom.
 *
 * ⚠️ **Its own tier check, and the page's is NOT enough.** `SettingsPage` gates
 * the mailer on `ability.isAdmin` and the sidebar row is `adminOnly`, so an
 * ordinary `admin` reaches this page legitimately — and an ordinary `admin` may
 * not start a support session. Inheriting the page's gate would hand the whole
 * tier a control the owner scoped to `super_admin`, silently, with nothing
 * failing. The card is withheld entirely rather than rendered as a refusal,
 * which is how `/permission-sets` treats the same tier boundary: an admin has
 * no business being shown a feature they were never in scope for.
 */
function SupportSessionCard() {
  const { ability, user: actor, supportSession } = useAuth();
  const headingId = useId();

  const [search, setSearch] = useRememberedState('support-session-search', '');
  const [result, setResult] = useState<Paginated<User> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<User | null>(null);

  /*
   * Every fetch carries an `AbortSignal` and both handlers guard on it, and the
   * box debounces at 250ms — the house pattern, `ContactsPage` being the
   * reference. Without the debounce this fires a request per keystroke against
   * a bucket shared across the whole office.
   * ⚠️ *SUPERSEDED: "a **120-per-60s** bucket."* It is **12,000/60s** (corrected
   * 2026-09-17) — the debounce is the house pattern whatever the headroom, and a
   * request per keystroke is the wrong shape rather than an expensive one.
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const trimmed = search.trim();
      api
        .listUsers(
          {
            perPage: SUPPORT_SEARCH_PER_PAGE,
            ...(trimmed ? { search: trimmed } : {}),
          },
          controller.signal,
        )
        .then((data) => {
          if (controller.signal.aborted) return;
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          /* A 401 belongs entirely to `@/lib/api`, which refreshes once and
             then ends the session; a message here would contradict it midway
             through signing out. */
          if (caught instanceof ApiError && caught.status === 401) return;
          setError(caught instanceof Error ? caught.message : 'Could not read the staff list.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [search]);

  const rows = result?.data ?? [];
  const initialLoad = loading && result === null;

  return (
    <>
    <Panel aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title="Support sessions"
        actions={
          <div className="w-full sm:w-64">
            <Field label="Search staff" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search by name or email…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
            </Field>
          </div>
        }
      />
      <PanelBody className="flex flex-col gap-3">
        {error && <Notice title="Couldn’t read the staff list.">{error}</Notice>}

        {initialLoad ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {Array.from({ length: 4 }, (_, row) => (
              <li key={row} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="grid flex-1 gap-1.5">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-2.5 w-40" />
                </div>
                <Skeleton className="h-8 w-20" />
              </li>
            ))}
          </ul>
        ) : rows.length === 0 ? (
          /* An empty state saying what to do next — the house rule, and the one
             kind of description the no-helper-text prohibition keeps by name. */
          <EmptyState
            icon={<LifebuoyIcon />}
            title={search.trim() ? 'Nobody matches that search' : 'No one to show'}
            description={
              search.trim()
                ? 'Try a different name or email address, or clear the box to see everyone.'
                : error
                  ? 'The staff list couldn’t be loaded. Check the message above and try again.'
                  : 'People appear here as they are added to SEED EMERGE.'
            }
            {...(search.trim()
              ? {
                  action: (
                    <Button variant="secondary" size="sm" onClick={() => setSearch('')}>
                      Clear search
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <>
            <ul
              aria-busy={loading || undefined}
              className={cx(
                'm-0 flex list-none flex-col p-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
                loading && 'opacity-60',
              )}
            >
              {rows.map((row) => {
                const denial = supportSessionDenial(ability, actor, row, supportSession);
                return (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border py-2.5 last:border-b-0"
                  >
                    <Avatar name={row.name} size="md" />
                    <div className="min-w-0 flex-1 basis-40">
                      <p className="m-0 truncate text-sm font-medium text-fg">{row.name}</p>
                      {/* Emails have no spaces, so only anywhere-wrapping keeps
                          them inside 320px. */}
                      <p className="m-0 text-xs [overflow-wrap:anywhere]">
                        <EmailLink address={row.email} tone="muted" />
                      </p>
                    </div>
                    {/* Two tones for five roles, matching `/users`: `accent`
                        marks the admin tier and everything else is neutral. The
                        label carries the rest, so colour is never the only
                        signal — and here it is also the reason a row is held
                        down, which the sentence beside it states outright. */}
                    <Badge tone={isAdminRole(row.role) ? 'accent' : 'neutral'}>
                      {roleLabel(row.role)}
                    </Badge>
                    <div className="flex min-w-0 flex-1 basis-full items-center justify-end gap-2 sm:basis-auto">
                      {/* The reason is text, not only a `title`: a disabled
                          button has `pointer-events: none`, so its tooltip
                          never reaches the person it is written for. Without
                          it a held-down button makes the super-admin rule, your
                          own row and an ordinary one look identical. */}
                      {denial && (
                        <span className="min-w-0 text-xs text-fg-subtle [overflow-wrap:anywhere]">
                          {denial}
                        </span>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        leadingIcon={<LifebuoyIcon />}
                        disabled={denial !== null}
                        title={denial ?? undefined}
                        aria-label={`Act as ${row.name}`}
                        onClick={() => setTarget(row)}
                      >
                        Act as
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>

          </>
        )}
      </PanelBody>
    </Panel>

    {/*
      The reason box, the consequence and the start. Mounted here and closed by
      `user === null`, the same shape as the modals on `/users` — and the SAME
      component, so the sentence a reader agrees to cannot differ between the
      two entry points.
    */}
    <SupportSessionDialog user={target} onClose={() => setTarget(null)} />
    </>
  );
}

/* --- the default quote terms --------------------------------------------- */

/**
 * The sentence a non-admin sees instead of an enabled control.
 *
 * Written out rather than routed through `denialReason()`, for the reason the
 * mailer's own denial is: the helper's sentence for this module names editing in
 * general and not these terms. ⚠️ *SUPERSEDED 2026-09-02: "there is no `settings`
 * module in `ModuleSlug` and one must not be invented".* There is one now, and
 * the gate consults it — **as a narrower filter on top of the admin tier, never
 * instead of it.** Read the mailer's own note above for why that order is the
 * whole of it, and for why the permission half is inert today. The ability
 * object, never `user.role` and never `/permissions/me` directly.
 *
 * ⚠️ **This gates the WRITE only.** `GET /settings/quote-terms` is open to every
 * authenticated role, `viewer` included, deliberately: the quote form prefills
 * from it, so anybody who can raise a quote must be able to read the wording. So
 * a non-admin reaching this page SEES the terms and cannot change them, which is
 * the whole point of showing rather than hiding them.
 *
 * ⚠️ **The nav row for this page is `adminOnly` and that is NOT this card's
 * doing.** It follows `GET /settings/smtp`, which is admin tier on the read. So
 * in practice a non-admin arrives here only by typing the URL, and the ordinary
 * place they see this wording is the quote form itself, which prefills from the
 * same endpoint. Do not "fix" the discrepancy by hiding this card from them.
 */
// 2026-10-01: the set decides — see `testDenialFor`.
// 2026-10-02 (owner): quote terms are `settings-documents`.
function quoteTermsDenialFor(ability: Ability): string | null {
  return denialReason(ability, 'update', 'settings-documents');
}

/**
 * The wording every NEW quote starts from.
 *
 * ## The textarea binds to `quoteTerms`, NEVER to `effectiveQuoteTerms`
 *
 * ⚠️ The two differ exactly when nothing is configured, and **this box is the
 * CONFIGURED value** — so an organisation that has set nothing sees an empty
 * box, which is the truth. Binding it to the effective value would render the
 * shipped standard terms as though somebody here had written them, an
 * administrator could no longer tell *"not set"* from *"set to exactly the
 * standard wording"*, and the first save would silently promote a release
 * constant into stored policy.
 *
 * **The quote form does the exact opposite for the exact opposite reason** — see
 * `use-quote-terms-default.ts`. Swapping the two reproduces the bug this whole
 * feature exists to fix, in which the default was applied invisibly and people
 * retyped it. The standard terms appear here as the textarea's PLACEHOLDER when
 * nothing is configured, which says what a quote will carry without claiming it
 * as content: a placeholder is not submitted, so an untouched save still stores
 * nothing.
 *
 * ## Clearing the box does NOT mean new quotes carry no terms
 *
 * ⚠️ It restores the standard ones. Three of their lines — the Signature, Name
 * and Date lines — are DocuSign ANCHOR STRINGS, and a quote whose terms lack
 * them **cannot be sent for signature at all**. The hint under the control says
 * so in as many words, because a person emptying a box reasonably expects
 * emptiness, and that copy is the only thing standing between the expectation
 * and a quote nobody can sign.
 *
 * ⚠️ **Nothing validates those anchors on save**, here or on the server. A
 * wording change that drops or duplicates one is accepted, and the failure
 * surfaces later on a DocuSign send, on a quote somebody has already built. The
 * hint carries that warning too, rather than this comment keeping it privately.
 */
function QuoteTermsCard() {
  const { ability } = useAuth();
  const headingId = useId();

  const [settings, setSettings] = useState<QuoteTermsSettings | null>(null);
  /*
   * The edit buffer, and it is a plain string where the stored value is
   * `string | null`. The empty string and `null` collapse to ONE stored state
   * server-side, so a nullable draft would be a second representation of *not
   * set* on this side of the wire — precisely the thing the server refuses to
   * have, because the second one is the one somebody forgets to check.
   */
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback((signal: AbortSignal) => {
    return api
      .getQuoteTermsSettings(signal)
      .then((response) => {
        if (signal.aborted) return;
        setSettings(response.data);
        // Seeded from the CONFIGURED value, so `null` is an empty box.
        setDraft(response.data.quoteTerms ?? '');
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * ⚠️ There is deliberately NO 403 branch on this read, unlike the Email
         * card's. That endpoint is admin tier on the read and this one is not,
         * so a 403 here would mean the contract had changed under us — which
         * belongs in the error branch, where somebody will see it, rather than
         * being quietly rendered as a permission working correctly.
         *
         * A 401 is left entirely to `@/lib/api`, which attempts one refresh and
         * then ends the session; reporting it would put a second, contradictory
         * message on screen midway through signing out.
         */
        if (caught instanceof ApiError && caught.status === 401) return;
        setError(
          caught instanceof Error ? caught.message : 'Could not read the default quote terms.',
        );
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const denial = quoteTermsDenialFor(ability);
  const trimmed = draft.trim();

  function save() {
    // `saving` is the double-fire guard as well as the disabled reason. Set
    // before the call and cleared in `finally`, never on either branch alone.
    if (saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    setFieldError(null);
    api
      /*
       * An empty box is sent as an explicit `null`. The server normalises the
       * empty string to `null` anyway, so this changes no outcome — it means the
       * two ends agree IN WRITING about what an empty box means, rather than
       * this side depending on a normalisation it never states.
       */
      .updateQuoteTermsSettings({ quoteTerms: trimmed === '' ? null : draft })
      .then((response) => {
        setSettings(response.data);
        /*
         * Re-seeded from the RESPONSE, never from the draft. The server trims
         * and normalises, so the box must show what is STORED rather than what
         * was typed — and the response is the only place that difference is
         * visible.
         */
        setDraft(response.data.quoteTerms ?? '');
        setSaved(true);
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError) {
          /*
           * The one control on this card is named `quoteTerms`, and the server's
           * 422 binds to exactly that path — the key omitted, or the value over
           * the 16,000-character cap. Route it onto the field; everything
           * unroutable (an unrecognised key, a root-level refusal, a 403 with no
           * details at all) goes to the banner. That is the house rule: never
           * count an unbindable message toward "the field below is highlighted".
           */
          const routed = caught.routeFieldErrors(['quoteTerms']);
          const bound = routed.fields.quoteTerms;
          if (bound) setFieldError(bound);
          if (routed.unroutable.length > 0) {
            setError(routed.unroutable.join(' '));
          } else if (!bound) {
            setError(caught.message);
          }
          return;
        }
        setError(caught instanceof Error ? caught.message : 'Could not save the default terms.');
      })
      .finally(() => setSaving(false));
  }

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Default quote terms" />
      <PanelBody className="flex flex-col gap-4">
        {error && <Notice title="Couldn’t save the default quote terms.">{error}</Notice>}

        {loading && settings === null ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-72" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : settings === null ? null : (
          <>
            <Field
              label="Terms"
              hideLabel
              hint="Empty goes back to the standard terms. Keep the Signature, Name and Date lines, once each — without them a quote cannot be sent for signature."
              error={fieldError ?? undefined}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  rows={12}
                  value={draft}
                  maxLength={16_000}
                  disabled={denial !== null || saving}
                  invalid={Boolean(fieldError)}
                  aria-describedby={describedBy}
                  /*
                   * The standard terms stand in as a PLACEHOLDER when nothing is
                   * configured — visible, and not submitted. It is the one way
                   * to show what a quote will actually carry without the box
                   * claiming it as saved policy.
                   */
                  placeholder={settings.isConfigured ? undefined : settings.effectiveQuoteTerms}
                  onChange={(event) => {
                    setSaved(false);
                    setFieldError(null);
                    setDraft(event.target.value);
                  }}
                />
              )}
            </Field>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={save}
                disabled={denial !== null || saving}
                title={denial ?? undefined}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}
              {saved && !denial && <p className="m-0 text-xs text-fg-subtle">Saved.</p>}
              {settings.updatedAt && (
                <p className="m-0 text-xs text-fg-subtle">
                  {/*
                    "Last changed", never "last saved". The column is
                    ON UPDATE CURRENT_TIMESTAMP, so re-saving byte-identical
                    wording leaves this — and the name beside it — exactly where
                    they were. The pair answers *when did this wording last
                    differ*, never *when did somebody last press save*.
                  */}
                  {`Last changed ${formatDateTime(settings.updatedAt)}`}
                  {settings.updatedBy ? ` by ${settings.updatedBy.name}` : ''}
                </p>
              )}
            </div>
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

/* --- the signature email ------------------------------------------------- */

/**
 * The sentence a non-admin sees instead of an enabled control.
 *
 * Written out rather than routed through `denialReason()`, for the reason the
 * two cards above do it: the helper's sentence for this module names editing in
 * general and not this wording. The ability object, never `user.role` and never
 * `/permissions/me` directly.
 *
 * ⚠️ **THE ADMIN TIER IS THE FLOOR AND THE PERMISSION IS A NARROWER FILTER ON
 * TOP** — the order the mailer card above states at length, and the same order
 * here for the same reason: `PATCH /settings/docusign-templates` is admin-only,
 * so a set granting `settings.update` to a `basic_user` must not open this.
 *
 * ⚠️ **This gates the WRITE only.** The read is open to every authenticated
 * role, deliberately — an envelope carries no record of the wording it was sent
 * with, so hiding the boxes would leave what a signer received unattributable to
 * everybody except the few who may change it. The DocuSign `viewer` shape (a
 * refusal about THIS reader), not the Xero `sync` one (an affordance whose only
 * outcome is an error, withheld entirely).
 */
// 2026-10-01: the set decides — see `testDenialFor`.
// 2026-10-02 (owner): the DocuSign templates are `settings-documents`.
function signatureEmailDenialFor(ability: Ability): string | null {
  return denialReason(ability, 'update', 'settings-documents');
}

/**
 * The keys this form writes, and therefore the list a `422` is partitioned
 * against.
 *
 * ⚠️ **Both controls are on screen whether or not either was SENT**, which is
 * why this is the form's own key list rather than the changed keys — the
 * narrower list `SheetFillColoursCard` uses is right there because its controls
 * come and go with the server's map, and wrong here. A key bound to a control
 * that exists is safe to mark and to count; anything else goes to the banner.
 */
const SIGNATURE_WRITE_KEYS = ['subject', 'message'] as const;

/**
 * The subject and message DocuSign puts in front of a signer.
 *
 * ## Why the placeholder vocabulary is on screen at all
 *
 * ⛔ The no-helper-text prohibition forbids prose attached to a control. This
 * card carries exactly **two** such sentences, and both are the shape the rule
 * keeps by name: the `hint` on each box says what an EMPTY one comes to, which
 * is a state the control cannot express and which mirrors the one line that
 * survived the sweep on {@link QuoteTermsCard}.
 *
 * The placeholder vocabulary is deliberately **not** a third. It is a
 * **reference list**, and the distinction is the prohibition's own test — *what
 * wrong conclusion does this prevent?*
 *
 * Without it a reader either does not know substitution exists (and types
 * "Dear customer", so the feature quietly does not do the thing it is for), or
 * knows it does and **guesses a name** — `{{firstName}}`, `{{quoteRef}}`,
 * `{{company}}`.
 *
 * 🔴 **AND A GUESS IS REMOVED, NOT REFUSED AND NOT PRINTED — measured in
 * `fillTemplate`, not assumed.** ⚠️ *An earlier draft of this comment said an
 * unknown token reaches the customer as raw braces; it does not, and the real
 * behaviour is worse for being tidier.* That function gates on the placeholder
 * SHAPE rather than on the token list, so anything of the form `{{…}}` it cannot
 * resolve is deleted along with the whitespace around it. `Please sign quote
 * {{quoteRef}}` goes out as `Please sign quote` — a clean sentence with the one
 * fact missing, no braces to notice, no refusal, nothing on this screen, and
 * nothing in the sent mail to say a word was dropped.
 *
 * ⚠️ **So the failure is deferred AND silent**, which is exactly the anchor-string
 * warning on {@link QuoteTermsCard} — the one line that survived the sweep there.
 * It is on the exception list by the same clause: it names **a state the reader
 * cannot otherwise see**, which is the set of names that will actually be
 * substituted. Nothing else on this card can tell them.
 *
 * ⚠️ **It is a GROUP LABEL over DATA, never advice**, which is what keeps it
 * inside the rule rather than carved out of it. The label is the whole claim —
 * *the only placeholders SEED EMERGE fills in* — so there is no second sentence
 * explaining the first, and the rows beneath it are a `dl` of token against what
 * it stands for, the same treatment the mailer card gives the env keys the
 * server is missing.
 *
 * ⚠️ **The claim is POSITIVE on purpose.** Naming the closed set asserts only
 * what the exported list already says, and it stays true whatever substitution
 * does with a name that is not on it — which is what the first draft of this
 * comment got wrong. A sentence describing the failure would have had to be
 * rewritten when the behaviour was read properly; the label did not.
 *
 * ⛔ **It is NOT a `hint`.** A `Field` renders `hint && !error`, so the
 * vocabulary would vanish at exactly the moment a refusal is on screen — which
 * is the moment somebody is most likely to be looking for it — and two controls
 * sharing one vocabulary would print it twice.
 *
 * ⚠️ **The list is derived from `DOCUSIGN_TOKENS`**, so a token added there
 * appears here with no edit and no count anywhere to go stale.
 *
 * ## What is deliberately NOT said
 *
 * ⚠️ **That DocuSign sends this mail rather than the relay the Email card below
 * reports on.** It was weighed rather than overlooked: a reader who assumes
 * otherwise goes and looks at that card, which truthfully reports its own state,
 * and sets the wording here either way — so the wrong conclusion costs nothing,
 * where the vocabulary's costs a customer-facing email. **The test is what a
 * sentence PREVENTS, not whether it is true**, and this one prevents nothing.
 */
function SignatureEmailCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const tokensGroupId = useId();

  /**
   * ⚠️ **THE BOXES BIND TO THE CONFIGURED VALUES, not to a resolved effective
   * one** — the same ruling as {@link QuoteTermsCard} and the reason
   * `DocuSignEmailTemplates` states at its own fields. An organisation that has
   * set nothing sees an empty box, which is the truth, and an administrator can
   * tell *"not set"* from *"set to wording identical to the default"*. Binding to
   * `resolveTemplate(...)` would freeze the shipped default into their
   * configuration the first time anybody pressed Save on an untouched form.
   */
  const [settings, setSettings] = useState<DocuSignEmailTemplates | null>(null);
  /*
   * The edit buffers, plain strings where the stored values are `string | null`.
   * Empty and `null` mean ONE stored state, so a nullable draft would be a
   * second representation of *not set* on this side of the wire — and the second
   * one is the one somebody forgets to check.
   */
  const [subjectDraft, setSubjectDraft] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [loading, setLoading] = useState(true);
  /**
   * ⚠️ **THE TITLE TRAVELS WITH THE MESSAGE.** One banner serves the read and
   * the write, and the two want opposite sentences: "couldn't save" over a
   * failed READ sends somebody hunting for a save they never made. The card
   * above carries a fixed title for the same banner and is wrong in exactly that
   * way; `SheetFillColoursCard` fixed it, and this follows the fix rather than
   * the older sibling.
   */
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /** A 404 — the API predates this endpoint. Version skew, not a failure. */
  const [missing, setMissing] = useState(false);

  const seed = useCallback((data: DocuSignEmailTemplates) => {
    setSettings(data);
    // Seeded from the CONFIGURED values, so `null` is an empty box.
    setSubjectDraft(data.subject ?? '');
    setMessageDraft(data.message ?? '');
  }, []);

  const load = useCallback(
    (signal: AbortSignal) =>
      api
        .docusignTemplates(signal)
        /*
         * ⚠️ **THE BARE SHAPE, NOT AN `Envelope` — unlike every `/settings/*`
         * neighbour on this page**, and `api.ts` says so at the method: there is
         * no `meta` to carry, and `readDocusignTemplates` unwraps and PROVES both
         * keys at the border, which is what lets the type declare them required.
         * So there is no `.data` here and adding one is a compile error.
         */
        .then((templates) => {
          if (signal.aborted) return;
          seed(templates);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          /*
           * ⚠️ **A 404 IS THE ORDINARY ANSWER FROM A SERVER PREDATING THIS
           * ROUTE**, not a fault — this app deploys separately from the API and
           * can be newer than it. It renders the empty state below rather than a
           * red banner about a mailer nobody asked after.
           *
           * ⚠️ There is deliberately NO 403 branch, unlike the Email card's:
           * that endpoint is admin tier on the READ and this one is not, so a
           * 403 here would mean the contract had changed under us — which
           * belongs where somebody will see it, not quietly rendered as a
           * permission working correctly.
           *
           * A 401 is left entirely to `@/lib/api`, which attempts one refresh
           * and then ends the session; a message here would contradict it midway
           * through signing out.
           */
          if (caught instanceof ApiError && caught.status === 404) {
            setMissing(true);
            return;
          }
          if (caught instanceof ApiError && caught.status === 401) return;
          setError({
            title: 'Couldn’t read the signature email.',
            message: caught instanceof Error ? caught.message : 'The server did not answer.',
          });
        })
        .finally(() => {
          if (signal.aborted) return;
          setLoading(false);
        }),
    [seed],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const denial = signatureEmailDenialFor(ability);

  /**
   * What will actually be sent — **one derivation, so the Save button's enabled
   * state and the payload can never disagree.** Two of them is how a Save that
   * is offered sends `{}`, which this API answers `422`.
   *
   * ⚠️ **An emptied box is an explicit `null`, which RESTORES the standard
   * wording rather than clearing it.** Omitting the key instead would leave the
   * old wording stored while the box on screen reads empty — the save looks like
   * it worked and changed nothing.
   *
   * ⚠️ **Only the EMPTINESS test is trimmed; the value sent is the draft as
   * typed.** The card above does the same: trimming what is stored would quietly
   * rewrite somebody's deliberate leading blank line, and the server's own
   * normalisation is visible in the response rather than guessed at here.
   */
  const patch = useMemo<DocuSignEmailTemplatesUpdateInput>(() => {
    const next: DocuSignEmailTemplatesUpdateInput = {};
    if (settings === null) return next;
    const subject = subjectDraft.trim() === '' ? null : subjectDraft;
    const message = messageDraft.trim() === '' ? null : messageDraft;
    if (subject !== settings.subject) next.subject = subject;
    if (message !== settings.message) next.message = message;
    return next;
  }, [settings, subjectDraft, messageDraft]);

  const changedKeys = Object.keys(patch);

  function save() {
    // `saving` is the double-fire guard as well as the disabled reason. Set
    // before the call and cleared in `finally`, never on either branch alone.
    if (saving || settings === null || changedKeys.length === 0) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    setFieldErrors({});
    api
      /*
       * ⚠️ **A DIFF, never the whole loaded pair.** An omitted key leaves that
       * template alone; a `null` puts it back to the compiled default. Sending
       * both every time is a `200` that turns an untouched default into a stored
       * choice — invisible until a release moves the standard wording and this
       * organisation's copy silently stops following it.
       */
      .setDocusignTemplates(patch)
      .then((templates) => {
        /*
         * Re-seeded from the RESPONSE, never from the drafts. The server trims
         * and normalises `''` to `null`, so the boxes must show what is STORED
         * rather than what was typed — and the response is the only place that
         * difference is visible. The bare shape again, so no `.data`.
         */
        seed(templates);
        setSaved(true);
      })
      .catch((caught: unknown) => {
        if (!(caught instanceof ApiError)) {
          setError({
            title: 'Couldn’t save the signature email.',
            message: caught instanceof Error ? caught.message : 'The server did not answer.',
          });
          return;
        }
        /*
         * Routed against this form's own write keys, so a refusal naming a
         * control on screen marks it and everything else lands in the banner —
         * never on an off-screen input, where it would inflate a "one field
         * needs attention" count and highlight nothing.
         *
         * A 403 is a live possibility rather than a formality: the gate above is
         * an optimisation and a role can be taken away under a token that lasts
         * over five days. It carries no `details`, so it lands here as a banner.
         */
        const routed = caught.routeFieldErrors(SIGNATURE_WRITE_KEYS);
        setFieldErrors(routed.fields);
        if (routed.unroutable.length > 0) {
          setError({
            title: 'Couldn’t save the signature email.',
            message: routed.unroutable.join(' '),
          });
        } else if (Object.keys(routed.fields).length === 0) {
          setError({ title: 'Couldn’t save the signature email.', message: caught.message });
        }
      })
      .finally(() => setSaving(false));
  }

  function editSubject(value: string) {
    setSaved(false);
    setFieldErrors((current) => {
      if (current['subject'] === undefined) return current;
      const next = { ...current };
      delete next['subject'];
      return next;
    });
    setSubjectDraft(value);
  }

  function editMessage(value: string) {
    setSaved(false);
    setFieldErrors((current) => {
      if (current['message'] === undefined) return current;
      const next = { ...current };
      delete next['message'];
      return next;
    });
    setMessageDraft(value);
  }

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Signature email" />
      <PanelBody className="flex flex-col gap-4">
        {error && <Notice title={error.title}>{error.message}</Notice>}

        {loading && settings === null && !missing ? (
          /* Skeletons in the real shape — a label and a line, then a label and a
             block, then the placeholder table. */
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-9 w-full" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-24 w-full" />
            </div>
            <Skeleton className="h-3 w-56" />
          </div>
        ) : missing ? (
          /*
           * ⚠️ **"This server does not have it", never "nothing is set".** An
           * unconfigured deployment answers `200` with two nulls, so a 404 can
           * only mean a build older than this one — version skew, an ordinary
           * thing to be in the middle of and not a fault to report in red.
           * Everything below is skipped: a save against a route this server does
           * not have would only collect a second 404.
           */
          <EmptyState
            icon={<MailIcon />}
            title="This API has no signature email settings"
            /*
             * ⚠️ Says only what THIS card knows. A sentence about what a send
             * falls back to would be a claim about another surface's behaviour
             * against a server nobody here has measured.
             */
            description="The server is running a build older than this one, so the wording cannot be read or changed here."
          />
        ) : settings === null ? (
          /*
           * ⚠️ **A FAILED READ GETS THE BANNER AND NOTHING ELSE.** An empty state
           * here would be a positive claim about the server, and a request that
           * never came back is not evidence for one.
           */
          null
        ) : (
          <>
            <Field
              label="Subject"
              hint="Empty goes back to the standard subject."
              error={fieldErrors['subject']}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={subjectDraft}
                  disabled={denial !== null || saving}
                  invalid={fieldErrors['subject'] !== undefined}
                  /*
                   * ⚠️ **UNCONDITIONAL, and that is a deliberate divergence from
                   * {@link QuoteTermsCard}, which gates its placeholder on
                   * `isConfigured`.** Two reasons, and the second is the one that
                   * matters:
                   *
                   * 1. **There is nothing to gate it against.** That card's
                   *    placeholder is `effectiveQuoteTerms`, which EQUALS the
                   *    configured wording once there is some — so showing it
                   *    unconditionally would repeat the box's own content back at
                   *    it. This one is a compiled constant, and a placeholder only
                   *    paints on an EMPTY box, where the default is what a send
                   *    will use. That is true in every state, including the one
                   *    the gate would get wrong: configured, then cleared, not
                   *    yet saved — which is exactly when somebody needs to see
                   *    what clearing comes to.
                   * 2. ⛔ **The gate would have to be `settings.subject === null`,
                   *    and `resolveTemplate` forbids that test in terms.** `''`
                   *    and `'   '` also fall back to the default, and `''` can
                   *    reach here — the server normalises it, but
                   *    `readDocusignTemplates` only proves the key is a string.
                   *    A `=== null` gate would hide the placeholder for a value
                   *    that behaves exactly like `null`, so the box would show
                   *    nothing at all while a send used the standard wording.
                   *
                   * It is not content: a placeholder is never submitted, so an
                   * untouched save still stores nothing.
                   */
                  /*
                   * ⚠️ **No `maxLength`, deliberately.** The cap is the server's
                   * and is not in the contract, so a guess here is wrong in one
                   * of two ways: tighter than the real one silently truncates a
                   * legitimate subject as somebody types, looser does nothing at
                   * all. The `422` names the field and says the real limit.
                   */
                  placeholder={DEFAULT_SIGNATURE_SUBJECT}
                  onChange={(event) => editSubject(event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Message"
              hint="Empty goes back to the standard message."
              error={fieldErrors['message']}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  /*
                   * ⚠️ **Sized to the PLACEHOLDER, not to a guess at how much
                   * somebody will type.** `DEFAULT_SIGNATURE_MESSAGE` is eleven
                   * lines, and a textarea clips a placeholder rather than
                   * scrolling it — so at the card's commonest state, an
                   * unconfigured deployment with an empty box, a shorter control
                   * would cut the standard wording off mid-sentence with nothing
                   * to say it had.
                   */
                  rows={10}
                  aria-describedby={describedBy}
                  value={messageDraft}
                  disabled={denial !== null || saving}
                  invalid={fieldErrors['message'] !== undefined}
                  placeholder={DEFAULT_SIGNATURE_MESSAGE}
                  onChange={(event) => editMessage(event.target.value)}
                />
              )}
            </Field>

            {/*
             * The vocabulary. See this component's header for why it is here at
             * all, why the label carries the whole claim, and why it is not a
             * `hint` on either control above.
             *
             * ⚠️ **Rendered for every reader, including one who may not edit.**
             * The braces are visible in the boxes above whatever the permission
             * says, and a reader who cannot see what they stand for is left with
             * wording they cannot make sense of — the same ruling the sheet
             * colours card takes about showing values it will not let you write.
             */}
            <div
              role="group"
              aria-labelledby={tokensGroupId}
              className="flex flex-col gap-2 border-t border-border pt-4"
            >
              <p
                id={tokensGroupId}
                className="m-0 text-xs font-medium text-fg-subtle"
              >
                The only placeholders SEED EMERGE fills in
              </p>
              <dl className="m-0 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                {DOCUSIGN_TOKENS.map((token) => {
                  /*
                   * ⛔ **`tokenPlaceholder`, NEVER braces built here.** That
                   * helper is the one place the syntax is spelled, and this
                   * screen is the reason it exists: whatever a reader sees here
                   * is what they will type, so a list that disagreed with the
                   * matcher by one character would hand out a placeholder that
                   * resolves to nothing and is then deleted from the mail.
                   */
                  const text = tokenPlaceholder(token);
                  return (
                    <div key={text} className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                      <dt className="m-0">
                        {/*
                         * Real, selectable text in a `code` element — somebody
                         * is going to copy this. Monospace because the braces
                         * are load-bearing characters rather than decoration.
                         */}
                        <code className="font-mono text-xs text-fg-secondary [overflow-wrap:anywhere]">
                          {text}
                        </code>
                      </dt>
                      {/*
                       * No fallback for a missing label, and it is not needed:
                       * `DOCUSIGN_TOKEN_LABELS` is an exhaustive `Record` over
                       * the same union this list iterates, so a gap is a compile
                       * error in that module rather than a hole here — and an
                       * unreachable branch written as a guard reads to the next
                       * person as a live rule.
                       */}
                      <dd className="m-0 min-w-0 text-xs text-fg-subtle [overflow-wrap:anywhere]">
                        {DOCUSIGN_TOKEN_LABELS[token]}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
              {/*
               * ⚠️ **The `title` is on a WRAPPER, not on the button.** `Button`
               * carries `disabled:pointer-events-none`, so a disabled one never
               * receives the hover and its own `title` never renders — for
               * exactly the person it is written for.
               */}
              <span title={denial ?? undefined}>
                <Button
                  onClick={save}
                  disabled={denial !== null || saving || changedKeys.length === 0}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </span>
              {/*
               * The reason is text as well as the `title` above: without it a
               * greyed control makes a permission denial, a save in flight and
               * nothing-having-changed look identical.
               */}
              {denial && <p className="m-0 text-xs text-fg-subtle">{denial}</p>}
              {saved && !denial && (
                <p role="status" className="m-0 text-xs text-fg-subtle">
                  {/*
                   * ⚠️ **The consequence rides in the OUTCOME, never as a
                   * standing line under the controls** — the arrangement the
                   * owner left in place on `TrackerSheetPanel` when the standing
                   * sentence there was removed. What it prevents: somebody
                   * fixing a typo and believing the customer who already has the
                   * envelope will see the fix.
                   *
                   * ⚠️ The claim is the CONTRACT's, not a guess about DocuSign:
                   * `setDocusignTemplates` states it at the method — *"this
                   * changes NO envelope already sent … an email already in a
                   * customer's inbox is exactly as it was."*
                   */}
                  Saved. Anything already out for signature keeps the wording it was sent with.
                </p>
              )}
            </div>
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

/* --- tabs ---------------------------------------------------------------- */

type SettingsTab = 'documents' | 'email' | 'notifications' | 'advanced';

/*
 * The module each tab reads — owner's decision 2026-10-02, which split
 * `settings` into four. `advanced` is NOT read-gated: its sheet-colours card
 * reads an endpoint open to every role and gates its own write on `settings`,
 * and the support-session card is its own module and gates itself.
 */
const TAB_MODULE: Partial<Record<SettingsTab, ModuleSlug>> = {
  documents: 'settings-documents',
  email: 'settings-email',
  notifications: 'settings-notifications',
};

/**
 * ⚠️ **THE GROUPING IS A DECISION, NOT AN ARRANGEMENT, AND TWO OF THE FOUR TABS
 * EXIST TO KEEP TWO CARDS APART.**
 *
 * Owner's decision, 2026-09-19, taken against a three-tab alternative that put
 * the recipients card in with the mailer. **`Notifications` is separate from
 * `Email` for exactly the reason the card's own mount comment gives**: directly
 * under the relay, a list of addresses reads as *where outbound mail goes* — a
 * claim about every message this system sends rather than about the one
 * notification those addresses actually receive.
 *
 * ⛔ **So do not "tidy" these two together, however much one tab holding one card
 * looks like over-tabbing.** The adjacency this file refused in a single column
 * is the same adjacency a shared tab reintroduces; the tab is the column.
 *
 * ⚠️ **`advanced` IS A DELIBERATE JUNK DRAWER AND IS WORDED AS ONE.** Sheet fill
 * colours and support sessions have nothing to do with each other, and inventing
 * a theme that covered both would put a name on this tab that predicts the wrong
 * thing about whatever lands in it next.
 */
const SETTINGS_TABS: readonly { value: SettingsTab; label: string }[] = [
  { value: 'documents', label: 'Documents' },
  { value: 'email', label: 'Email' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'advanced', label: 'Advanced' },
];

const SETTINGS_TAB_PARAM = 'tab';

/**
 * 🔴 **A POSITIVE TEST WITH A FALLBACK, NEVER A NEGATION.**
 *
 * `raw === 'documents' ? … : 'email'` would land a stale or mistyped `?tab=` on
 * whichever tab the negation happened to name — the `/sales-tracker` hazard this
 * codebase already records, where `parseView` written as a negation would have
 * put every stale `?view=board` link on the sheet mirror instead of the table.
 *
 * ⚠️ **AN UNRECOGNISED VALUE FALLS BACK TO `documents` RATHER THAN THROWING OR
 * EMPTYING THE PAGE.** A settings screen rendering nothing because somebody
 * pasted a bad query string is indistinguishable from one that failed to load.
 */
function parseSettingsTab(raw: string | null): SettingsTab {
  if (raw === 'email') return 'email';
  if (raw === 'notifications') return 'notifications';
  if (raw === 'advanced') return 'advanced';
  return 'documents';
}

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`** — copied from
 * `CommissionsPage`'s `TabToggle` rather than re-derived, because this app
 * already answered the question: these switch what the section below IS, and a
 * real `tablist` would promise arrow-key navigation between panels that do not
 * share a tabstop.
 *
 * ⚠️ **Every pill renders at every width, always.** This codebase has had a
 * working feature reported as *deleted* when it had merely become unreachable
 * behind a toggle. The selected pill is not distinguished by colour alone:
 * `secondary` gives it a raised surface against `ghost`'s transparent one, and
 * `aria-pressed` states it outright.
 */
function SettingsTabs({
  value,
  onChange,
}: {
  value: SettingsTab;
  onChange: (next: SettingsTab) => void;
}) {
  return (
    <ViewToggle
      label="Settings section"
      value={value}
      options={SETTINGS_TABS}
      onChange={onChange}
      /*
       * Rendered through `ui/ViewToggle` since 2026-10-11, which carries the
       * `max-w-full flex-wrap` / no-`shrink-0` rule below as its own contract.
       *
       * 🔴 **`max-w-full` AND NO `shrink-0` — AND THAT IS A DELIBERATE DIVERGENCE
       * FROM THE `CommissionsPage` TOGGLE THIS IS OTHERWISE COPIED FROM.**
       *
       * That one carries `shrink-0` and is right to: it holds TWO pills and
       * shares a row with other controls, so it must not be squeezed. **Four
       * pills cannot fit 320px**, and `shrink-0` on a flex item means the box
       * never becomes narrow enough for its own `flex-wrap` to fire — so the
       * children stay on one row and the last pill is CLIPPED by an ancestor
       * rather than wrapped.
       *
       * ⚠️ **MEASURED, NOT REASONED: at 320 the `Advanced` pill ran to x=344,
       * with `scrollX` still 0 after a real wheel gesture** — present in the DOM,
       * off the viewport, and reachable by nothing. That is this codebase's
       * worst-recorded UI failure mode: a working feature reported as DELETED
       * because it had merely become unreachable.
       *
       * ⛔ Do not "harmonise" this back to the commissions class string, and
       * pass no `shrink-0` through `className`.
       */
    />
  );
}

/* --- page ---------------------------------------------------------------- */

export function SettingsPage() {
  const { ability } = useAuth();

  /*
   * ⚠️ **Read at the PAGE, so the card and its dialog never mount for a reader
   * who is not in scope** — an unmounted modal cannot be opened by a stale bit
   * of state, and an absent card cannot be reasoned about as a broken one. See
   * {@link SupportSessionCard} for why the page's own `adminOnly` gate is not
   * enough: `admin` and `super_admin` are the same tier everywhere else in this
   * app, and this is one of the two places they are not.
   */
  const showSupport = canOfferSupportSessions(ability);

  /*
   * ⚠️ **`useSearchParams()` WITH NO ARGUMENT.** Its first parameter is a DEFAULT
   * INIT rather than options, so `useSearchParams({ replace: true })` injects a
   * literal `replace=true` into the query string and then into anything parsing
   * it. The house rule, stated once in *Data fetching* and broken here by nobody.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The default is spelled by the key's ABSENCE and NOTHING is written on
   * arrival — an arrival write would put a Back-button step and a query string
   * in front of somebody who merely opened Settings.
   */
  const tab = parseSettingsTab(searchParams.get(SETTINGS_TAB_PARAM));
  const tabModule = TAB_MODULE[tab];
  const tabReadable = tabModule === undefined || ability.canRead(tabModule);

  const changeTab = useCallback(
    (next: SettingsTab) => {
      /*
       * ⚠️ **THERE IS NO `CLEARED_ON_SWITCH` HERE, AND THE ABSENCE IS MEASURED
       * RATHER THAN FORGOTTEN.** `CommissionsPage` — which this toggle is copied
       * from — clears six keys on every switch, because both of its sections read
       * filters and a pager out of one shared query string, and a key left behind
       * applies one section's narrowing to the other's list.
       *
       * **No card on this page reads the query string at all.** Each holds its own
       * state and saves through its own endpoint, so there is nothing a stale key
       * could narrow and nothing to clear. ⛔ **The day a settings card gains a
       * URL-backed control, this is where that list goes** — and until then an
       * empty clear-list would be a guard that cannot fire, reading to the next
       * person as a live rule.
       */
      const params = new URLSearchParams(searchParams);
      if (next === 'documents') params.delete(SETTINGS_TAB_PARAM);
      else params.set(SETTINGS_TAB_PARAM, next);
      /*
       * `{ replace: true }`: switching a pill adjusts what is on screen rather
       * than navigating, so it must not put a Back step between the reader and
       * wherever they arrived from.
       */
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /*
   * Layout manager (owner 2026-10-20): one per-person layout per tab, all
   * hooks called unconditionally. `email` holds a single card, so it has no
   * layout. The defaults reproduce the column each tab rendered before.
   */
  const documentsLayout = usePageLayout('settings.documents', [
    { id: 'quote-terms', label: 'Default quote terms' },
    { id: 'signature-email', label: 'Signature email' },
  ]);
  const notificationsLayout = usePageLayout('settings.notifications', [
    { id: 'notification-rules', label: 'Routing rules' },
    { id: 'notification-defaults', label: 'Team defaults' },
    { id: 'b2b-proposal-routing', label: 'B2B proposal routing' },
    { id: 'enquiry-alert-recipients', label: 'Enquiry popups' },
    { id: 'enquiry-assignees', label: 'Enquiry assignees' },
  ]);
  const advancedLayout = usePageLayout('settings.advanced', [
    { id: 'sheet-fill-colours', label: 'Client deliverables sheet colours' },
    { id: 'exchange-rates', label: 'Exchange rates' },
    { id: 'support-sessions', label: 'Support sessions' },
  ]);
  const activeLayout: PageLayout | null = !tabReadable
    ? null
    : tab === 'documents'
      ? documentsLayout
      : tab === 'notifications'
        ? notificationsLayout
        : tab === 'advanced'
          ? advancedLayout
          : null;

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Settings"
        actions={activeLayout ? <LayoutToolbar layout={activeLayout} /> : undefined}
      />

      {/*
       * ⚠️ *SUPERSEDED, and the half that died is only the shape: "A column of
       * cards. A further one is another element here — no restructuring".* A
       * further card is now an entry in ONE tab, which is a decision about which
       * tab rather than no decision at all. **The half that stands: there is
       * still deliberately no placeholder for anything unbuilt, and no empty tab
       * reserved for one.**
       */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SettingsTabs value={tab} onChange={changeTab} />
      </div>

      {/*
       * 🔴 **EXACTLY ONE TAB MOUNTS, SO SWITCHING UNMOUNTS THE CARDS AND LOSES AN
       * UNSAVED EDIT. That cost is real, was weighed, and is stated rather than
       * hidden.** Every card here holds its own draft and its own Save, so a
       * half-typed set of quote terms is discarded by a tab switch exactly as it
       * would be by navigating away — and nothing warns first.
       *
       * The alternative is rendering all four and hiding three with CSS, which
       * was refused twice over: it fires every card's fetch on arrival, which is
       * most of what tabbing this page buys, and it leaves three forms' worth of
       * focusable, submittable state in the DOM where a reader cannot see it.
       * `/operations` mounts exactly one pill for the same two reasons.
       */}
      <div className="flex flex-col gap-4">
        {/*
         * 2026-10-02: a tab whose module this reader cannot read renders the
         * refusal instead of its cards, so no card issues a request that can
         * only answer 403. The pill itself still renders, per the rule above.
         */}
        {tabModule !== undefined && !tabReadable && (
          <Panel>
            <EmptyState
              title="This section is restricted"
              description={denialReason(ability, 'read', tabModule) ?? PERMISSION_SET_REFUSAL}
            />
          </Panel>
        )}
        {tab === 'documents' && tabReadable && (
          <>
            {/*
             * ⚠️ **The terms FIRST, then the signature email — the order the
             * thing happens in.** The wording a quote carries, then the mail that
             * asks somebody to sign it. ⚠️ *SUPERSEDED, the clause that placed
             * this card: "Between the terms and the mailer on purpose … it keeps
             * the one card about DocuSign's own mail out of the pair that would
             * otherwise read as two halves of the SMTP settings."* **That
             * argument is not overturned — it is now STRUCTURAL.** The mailer is
             * a tab away, so nothing can read these two as halves of it, and this
             * wording still never touches that relay: DocuSign sends it.
             * (2026-10-20: that order is now the layout DEFAULT; a person may
             * rearrange their own view through the layout manager.)
             */}
            <LayoutGrid
              layout={documentsLayout}
              render={{
                'quote-terms': () => <QuoteTermsCard />,
                'signature-email': () => <SignatureEmailCard />,
              }}
            />
          </>
        )}

        {tab === 'email' && tabReadable && <EmailCard />}

        {tab === 'notifications' && tabReadable && (
          <>
            {/*
             * 🔴 **A TAB OF ITS OWN, AND THAT IS THE WHOLE REASON IT EXISTS —
             * DO NOT FOLD IT INTO `Email`.** ⚠️ *The original wording, which is
             * unchanged in force and only in mechanism: "**DELIBERATELY NOT
             * BESIDE THE MAILER** … These addresses receive ONE internal
             * notification — the 2027 interest form's — and they really do go out
             * through the relay the Email card reports on, so a card sitting
             * directly under it reads as *the addresses outbound mail goes to*: a
             * claim about every email this system sends rather than about one of
             * them."*
             *
             * ⛔ **A SHARED TAB IS THE SAME ADJACENCY: THE TAB IS THE COLUMN.**
             * Putting these two together would reintroduce, through a grouping,
             * precisely the misreading the ordering was arranged to prevent —
             * which is why a one-card tab was chosen over a tidier three-tab set.
             *
             * ⚠️ *SUPERSEDED 2026-09-19, and ONLY the arithmetic: "**ONE internal
             * notification — the 2027 interest form's**", and "**a one-card tab**".*
             * **There are TWO cards here now** — the second is the B2B proposal
             * notice — and 🔴 **the argument above is STRENGTHENED rather than
             * weakened by that.** It was never "this one card has nowhere else to
             * go"; it is that a card reading *who internal mail is addressed to*
             * must not sit under the card reporting on the RELAY, or it reads as
             * a claim about every email this system sends. A second such card
             * under the mailer would make that misreading twice over. ⛔ **So the
             * tab is not a stopgap that a second card retires: this is the tab
             * those cards belong in, and the mailer is not.**
             *
             * ⚠️ **AND THE TWO ARE TWO SETTINGS, NOT ONE CARD WITH TWO USES —
             * owner decision, 2026-09-19 (backend migration 228).** The two lists
             * hold the same address today, which is exactly what makes merging
             * them dangerous: nothing enforces that, nothing records it, and one
             * administrator editing the festival list would silently re-address
             * the proposal notice too, with a `200`.
             *
             * ⚠️ **Mounted UNCONDITIONALLY, and each gates its own write.** Unlike
             * the cards in the other tabs their READ is admin tier too, so a
             * non-admin meets a `403`; each card renders that as a refusal about
             * their account rather than as an empty list, which would say the
             * opposite thing about whether anybody is being told.
             *
             * 🔴 *SUPERSEDED 2026-10-16 (notification store, owner-approved), the
             * three recipient-list cards above: `EventInterestRecipientsCard`,
             * `B2bProposalRecipientsCard`, `FutureBillableRecipientsCard`.* Their
             * lists were migrated into notification rules (backend migration
             * 287) and are edited in `NotificationRulesCard` now. The old card
             * files are left in the tree, unmounted. The tab's reason for being
             * apart from the mailer stands.
             *
             * B2B proposal GEOGRAPHY routing (`f_b2b_proposal_routing_rules`):
             * adds recipients when a proposal's country, country code or
             * continent matches. Kept beside the notification rules (owner
             * decision 2026-10-16). Admin tier on the read too, so a `403`
             * renders as a refusal, never as no rules.
             *
             * Enquiry alert recipients: who gets an in-app enquiry POPUP, per
             * billing region. ⚠️ **Not an email list** — these are staff user
             * ids, and the popup is filtered by each person's own read grant,
             * which the card says per person. Mounted unconditionally; its read
             * is admin tier, so it renders a `403` as a refusal about the reader,
             * never as an empty set.
             *
             * Enquiry assignees: the people an enquiry can be ASSIGNED to — free
             * text (name, email, team), not EMERGE users. Read open to any
             * signed-in reader; writes gate on `settings-notifications`. Removing
             * is soft: assigned enquiries keep a snapshot of the name.
             */}
            <LayoutGrid
              layout={notificationsLayout}
              render={{
                'notification-rules': () => <NotificationRulesCard />,
                'notification-defaults': () => <NotificationDefaultsCard />,
                'b2b-proposal-routing': () => <B2bProposalRoutingCard />,
                'enquiry-alert-recipients': () => <EnquiryAlertRecipientsCard />,
                'enquiry-assignees': () => <EnquiryAssigneesCard />,
              }}
            />
          </>
        )}

        {tab === 'advanced' && (
          <>
            {/*
             * ⚠️ **Mounted UNCONDITIONALLY, and it gates its own write** — the
             * shape every card on this page uses except the support one, which is
             * withheld because `super_admin` is a tier an ordinary `admin` has no
             * business being shown. This is not that: `GET
             * /client-deliverables/sheet/sort` is open to every authenticated
             * role, so a reader who may not change the colours must still SEE
             * them. A sheet carries no record of the colours that produced it, so
             * hiding the card would leave what is on screen unattributable to
             * everybody except the few who may edit it.
             *
             * ⚠️ **SUPPORT SESSIONS: WITHHELD RATHER THAN DISABLED, AND THE TAB
             * STAYS EITHER WAY.** `SheetFillColoursCard` is unconditional, so
             * `Advanced` can never render empty — which is what makes hiding the
             * support card safe. ⛔ A tab whose only card is conditional would
             * need the PILL hiding too, and a pill that disappears per reader is
             * how a feature gets reported as deleted. (2026-10-20: a person can
             * hide the colours card in their own layout; that is their choice and
             * the Hidden sections tray brings it back.)
             */}
            <LayoutGrid
              layout={advancedLayout}
              render={{
                'sheet-fill-colours': () => <SheetFillColoursCard />,
                'exchange-rates': () => <ExchangeRateSettings />,
                'support-sessions': () => (showSupport ? <SupportSessionCard /> : null),
              }}
            />
          </>
        )}
      </div>
    </>
  );
}
